import type {ReduxStore} from 'src/reducers';

import logger from 'src/utils/logger';
import {useHistory} from 'src/rerouter';
import {useRegisterAgenticUIIntent} from 'src/components/agentic-ui';
import {sendMessage, returnToNormalChat} from './ask-ai-store';
import {setAIPanelOpenEvent} from 'src/action-creators/sense-ai-panel';
import {AI_LISTERS_PATH} from 'src/utils/audience-ai-list';
import {FULLSCREEN_MODE_KEY} from 'src/utils/ask-ai';


const AUTOMATION_WORKFLOWS_PATH = '/automation-workflows';

export const useHandleAskAiEvents = (reduxStore: ReduxStore) => {
  const router = useHistory();

  useRegisterAgenticUIIntent('graceai', 'graceai.selectprompt', (intent) => {
    const prompt = (intent.payload as {prompt: string}).prompt;
    if (prompt) {
      sendMessage(prompt);
    }
  });
  // Inject backend-sent text into the chat as a user message.
  useRegisterAgenticUIIntent('graceai', 'graceai.sendmessage', (intent) => {
    const message = (intent.payload as {message: string}).message;
    if (message) {
      sendMessage(message);
    }
  });
  useRegisterAgenticUIIntent('graceai', 'graceai.generatecontent', (_) => {
    reduxStore.dispatch(setAIPanelOpenEvent(true));
  });
  // Action-card "Continue Conversation" (or a backend-emitted equivalent):
  // dismiss the docked sub-chat and return control to the composer.
  useRegisterAgenticUIIntent('graceai', 'graceai.continueconversation', () => {
    returnToNormalChat();
  });

  // Preview-card "open asset": route to the standalone asset viewer in
  // fullscreen chat mode, capturing the current page so close returns there.
  // We rely only on `asset_id` — the viewer fetches the asset by id.
  useRegisterAgenticUIIntent('graceai', 'graceai.resource.show', (intent) => {
    const {asset_id: assetId} = intent.payload as {asset_id?: string};
    if (!assetId) {
      return;
    }
    const sourceUrl = router.location.pathname;
    router.push({
      pathname: `/askai/asset-viewer/${assetId}`,
      query: {
        [FULLSCREEN_MODE_KEY]: 'true',
        sourceurl: sourceUrl,
      },
    });
  });

  // Cross-product navigation: the global handler only routes the user to the
  // target area, identified by the first key in `segment`. Once the destination
  // page mounts its own page-level handler, the user re-types their request
  // there. No buffering/replay here.
  useRegisterAgenticUIIntent('graceai', 'graceai.navigation', (intent) => {
    const {
      segment,
      workflow_id: workflowId,
      reference_id: listId,
    } = intent.payload as {
      segment?: string[];
      workflow_id?: string;
      reference_id?: string | number;
    };

    switch (segment?.[0]) {
      case 'automationWorkflow':
        router.push({
          pathname: workflowId
            ? `${AUTOMATION_WORKFLOWS_PATH}/${workflowId}`
            : AUTOMATION_WORKFLOWS_PATH,
        });
        break;
      case 'lists': {
        const query: Record<string, string> = {
          [FULLSCREEN_MODE_KEY]: 'true',
          sourceurl: router.location.pathname,
        };
        if (listId != null) {
          query.reference_id = String(listId);
        }
        router.push({pathname: AI_LISTERS_PATH, query});
        break;
      }
      case 'fieldSyncAudit':
        router.push({pathname: '/fields/field-sync'});
        break;
      default:
        logger.warning('Unhandled graceai.navigation segment', {segment});
    }
  });
};
